// packages/components/calendar/useCalendar.js
import { useMemo, useCallback } from "react";
import { VIEW_TYPES } from "./types";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  isSameDay,
  isSameMonth,
  format,
  startOfDay,
  addMonths,
  subMonths,
} from "date-fns";

/**
 * Hook to generate calendar data and handle calendar operations
 *
 * @param {Object} options
 * @param {Date} options.date - The current date
 * @param {function} options.setDate - Function to update the date
 * @param {Object} [options.events={}] - Events to display on the calendar
 * @param {boolean} [options.showAdjacentMonths=true] - Whether to show days from adjacent months
 * @param {string} [options.locale='en-US'] - Locale for date formatting
 * @returns {Object} Calendar data and handlers
 */
export default function useCalendar({
  date,
  setDate,
  events = {},
  showAdjacentMonths = true,
  locale = "en-US",
}) {
  // Generate days for the calendar grid
  const days = useMemo(() => {
    const result = [];
    const monthStart = startOfMonth(date);
    const monthEnd = endOfMonth(monthStart);
    const today = startOfDay(new Date());

    // Get the start of the week that contains the first day of the month
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });

    // Calculate the end of the week that contains the last day of the month
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

    // Generate days from start of first week to end of last week
    let currentDate = calendarStart;
    while (currentDate <= calendarEnd) {
      const dateString = format(currentDate, "yyyy-MM-dd");

      // Only add days if showAdjacentMonths is true or if the day is in the current month
      if (showAdjacentMonths || isSameMonth(currentDate, monthStart)) {
        result.push({
          date: dateString,
          isCurrentMonth: isSameMonth(currentDate, monthStart),
          isToday: isSameDay(currentDate, today),
          isSelected: isSameDay(currentDate, date),
          events: events[dateString] || [],
        });
      }

      currentDate = addDays(currentDate, 1);
    }

    // If not showing adjacent months, filter out days not in current month
    if (!showAdjacentMonths) {
      return result.filter((day) =>
        isSameMonth(new Date(day.date), monthStart)
      );
    }

    return result;
  }, [date, events, showAdjacentMonths]);

  // Handler for selecting a date
  const handleSelectDate = useCallback(
    (newDate) => {
      setDate(newDate);
    },
    [setDate]
  );

  // Handler for navigating to the previous month
  const handlePrevMonth = useCallback(() => {
    setDate(subMonths(startOfMonth(date), 1));
  }, [date, setDate]);

  // Handler for navigating to the next month
  const handleNextMonth = useCallback(() => {
    setDate(addMonths(startOfMonth(date), 1));
  }, [date, setDate]);

  // Handler for navigating to today
  const handleToday = useCallback(() => {
    setDate(startOfDay(new Date()));
  }, [setDate]);

  // Get the selected day
  const selectedDay = useMemo(() => {
    return days.find((day) => day.isSelected);
  }, [days]);

  return {
    days,
    selectedDay,
    handleSelectDate,
    handlePrevMonth,
    handleNextMonth,
    handleToday,
  };
}
